import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  inject,
  input,
  OnInit,
  output,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { TranslocoPipe } from '@jsverse/transloco';
import { DualLangInputComponent } from '../../../../../../../../../form-builder/lib/dual-lang-input/dual-lang-input.component';
import { DepartmentService } from '../../../../../departments/department.service';
import { SelectComponent } from '../../../../../../../../../ui-components/lib/select/select.component';
import { PaginatedSelectComponent } from '../../../../../../../../../ui-components/lib/paginated-select/paginated-select.component';
import { InputComponent } from '../../../../../../../../../ui-components/lib/input/input.component';
import { ValidationMessageComponent } from '../../../../../../../../../ui-components/lib/validation-message/validation-message.component';
import { OptionItem } from '../../../../../../../../../ui-components/lib/option.interface';
import {
  LookupFilter,
  LookupOptionItem,
} from '../../../../../../../../../ui-components/lib/lookup-field/lookup-field-item';
import { FormService } from '../../../../../../../../../form-builder/services/form/form.service';
import { dualLanguageCustomValidators } from '../../../../../../../../../form-builder/services/form/custom-validators';
import { FormValidation } from '../../../../../../../../../ui-components/enums/form-validation';
import { ToasterService } from '../../../../../../../../../ui-components/services/toast/toast.service';
import { filterInputTypesEnum } from '../../../../../../../../../ui-components/enums/filter-input-type';
import { ENDPOINTS } from '../../../../../../../../../core/common/constants/endpoints.constants';
import { Status } from '../../../../../../../../common/enums/user-status';
import { QuickCreatedItem } from './quick-create-model';

@Component({
  selector: 'lib-department-quick-create-form',
  imports: [
    ReactiveFormsModule,
    TranslocoPipe,
    DualLangInputComponent,
    SelectComponent,
    PaginatedSelectComponent,
    InputComponent,
    ValidationMessageComponent,
  ],
  templateUrl: './department-quick-create-form.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DepartmentQuickCreateFormComponent implements OnInit {
  private readonly departmentService = inject(DepartmentService);
  private readonly formService = inject(FormService);
  private readonly toasterService = inject(ToasterService);
  private readonly destroyRef = inject(DestroyRef);

  readonly legalEntity = input.required<OptionItem[]>();
  readonly prefillName = input<string>('');

  readonly created = output<LookupOptionItem>();
  readonly cancelled = output<void>();

  readonly isLoading = signal(false);

  protected readonly parentApi = ENDPOINTS.Lookup.Departments;
  protected readonly parentFilter: LookupFilter[] = [
    {
      key: 'legal_entity_id',
      value: 'legal_entity_id',
      value_type: filterInputTypesEnum.DYNAMIC,
      multiple: false,
      required: true,
    },
  ];
  protected readonly directManagerApi = `${ENDPOINTS.Lookup.User}?filters[not_states][0]=${Status.INACTIVE}&filters[not_states][1]=${Status.CANCELLED}&filters[not_states][2]=${Status.DELETED}`;
  protected readonly variableOptionsApi = ENDPOINTS.Lookup.VariableOptionsSorted;
  protected readonly departmentTypeFilter: LookupFilter[] = [
    {
      key: 'variable.module',
      value: 'user_management',
      value_type: filterInputTypesEnum.STATIC,
    },
    {
      key: 'variable.key',
      value: 'department_type',
      value_type: filterInputTypesEnum.STATIC,
    },
    { key: 'is_active', value: '1', value_type: filterInputTypesEnum.STATIC },
  ];

  readonly formGroup = new FormGroup({
    name: new FormGroup(
      {
        en: new FormControl('', [Validators.maxLength(FormValidation.maxLength)]),
        ar: new FormControl('', [Validators.maxLength(FormValidation.maxLength)]),
      },
      { validators: [dualLanguageCustomValidators.atLeastOneLangRequired] }
    ),
    identifier: new FormControl<string | null>(null, [
      Validators.maxLength(FormValidation.identifierMaxLength),
    ]),
    legal_entity_id: new FormControl('', Validators.required),
    parent_id: new FormControl<number | null>(null),
    direct_manager_id: new FormControl<number | null>(null),
    department_type_option_id: new FormControl<number | null>(null),
  });

  ngOnInit(): void {
    if (this.prefillName()) {
      this.formGroup.get('name.en')?.setValue(this.prefillName());
    }
  }

  submit(): void {
    this.formService.syncDualLangControls(this.formGroup);

    if (this.formGroup.invalid) {
      this.formGroup.markAllAsTouched();
      return;
    }

    this.isLoading.set(true);
    const value = this.formGroup.getRawValue();

    this.departmentService
      .addDepartment({
        name: { en: value.name?.en, ar: value.name?.ar },
        identifier: value.identifier ?? '',
        legal_entity_id: value.legal_entity_id,
        parent_id: value.parent_id ?? '',
        direct_manager_id: value.direct_manager_id ?? '',
        department_type_option_id: value.department_type_option_id,
      })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response: object) => {
          this.isLoading.set(false);
          const data = (response as { data: QuickCreatedItem }).data;
          this.created.emit({
            id: data.id,
            name: data.name,
            value: data.id,
            label: data.name,
          });
        },
        error: error => this.handleError(error),
      });
  }

  cancel(): void {
    this.cancelled.emit();
  }

  private handleError(error: {
    errors?: { [key: string]: string[] };
    message?: string;
    error?: { message?: string };
  }): void {
    this.isLoading.set(false);
    this.formService.addParentFormGroupErrors(
      error as { errors: { [key: string]: string[] }; message: string },
      this.formGroup
    );
    this.toasterService.add(
      error.message ? error.message : (error.error?.message ?? ''),
      '',
      '',
      'error',
      true,
      true
    );
  }
}
